import React, { useEffect, useRef } from 'react';
import { connect, useSelector } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleSpinner } from 'apps/auth/actions/appAction';
import { addExtras, updateExtraByName } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, setNewForm, updateFieldValues, updateFormProperties } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper, PanelContent, PanelFrame, PanelRightBlock } from 'apps/auth/components/common/Panel/styles';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import MainOfakimModal from 'apps/auth/components/send/ofakim/MainOfakimModal';
import OfakimDocsInfo from 'apps/auth/components/send/ofakim/OfakimDocsInfo';
import { INCREASE_LIMITS_SHOWN, METHOD_UNAVAILABLE_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import {
    GT_LEVEL,
    PAYMENT_COUNTRY,
    PAYMENT_CURRENCY,
    PAYMENT_METHOD_TYPE,
    PAYOUT_COUNTRY,
    PAYOUT_CURRENCY,
} from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { PAYMENT } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { PAYMENT_ACH, PAYMENT_BONUS, PAYMENT_GLOBAL_ACCOUNT } from 'apps/auth/constants/Constants';
import { COMMISSION, RECALCULATE } from 'apps/auth/constants/ExtraNames';
import { AMOUNT_FROM, AMOUNT_TO, DELIVERY_METHOD } from 'apps/auth/constants/fieldsName';
import { useApp, useExtra, useLanguage, useTypeOfTransfer } from 'apps/auth/hooks/selectors';
import { GTPaymentMethodService } from 'apps/auth/services/GTPaymentMethodService';

const getTitle = (name, description) => {
    if (description) {
        return (
            <>
                <Text color="lightGray">{name}</Text>
                <br />
                <Text color="lightGray" size="secondary">
                    {description}
                </Text>
            </>
        );
    } else {
        return <Text color="lightGray">{name}</Text>;
    }
};

const DisabledPaymentMethod = (props) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const { changeAmount, methodMinInfo, methodMaxInfo, balanceNotEnough, changePaymentOption } = useSelector(({ messages: { pageMessages } }) => pageMessages);
    const { toCountry, toCurrency, profile, userCountry, fromCurrency, incompleteTransfer } = useExtra();
    const deliveryMethod = useSelector(({ form }) => form[props.formName]?.fields[DELIVERY_METHOD]?.value);
    const paymentMethodService = useRef(null);
    const popupRef = useRef(null);
    const expectedGTLevel = profile?.amlLimit?.currentLimit + 1;
    const typeOfTransfer = useTypeOfTransfer();

    useEffect(() => {
        paymentMethodService.current = new GTPaymentMethodService(
            props.updateFieldValues,
            props.updateFormProperties,
            props.formName,
            toCountry?.code,
            toCurrency?.code,
            deliveryMethod,
            typeOfTransfer,
            incompleteTransfer,
        );
    }, []);

    const getName = (method = '', paymentMethod = '', paymentName = '') => {
        if (method === PAYMENT_BONUS || method === PAYMENT_GLOBAL_ACCOUNT) {
            return `${paymentMethod?.formattedBalance} ${paymentMethod?.currencyIso}`;
        }
        return paymentName;
    };

    const popupText = GTPaymentMethodService.getPopupTitle(props, methodMaxInfo, methodMinInfo, balanceNotEnough, props.bonusBalance, lang, country);

    const updateAmount = (isFrom) => {
        props.updateExtraByName(COMMISSION, props.commission).then(() => {
            props.updateExtraByName(RECALCULATE, true);
            props
                .changeFormFieldValue(props.formName, GTPaymentMethodService.getAmount(props, props.bonusBalance), isFrom ? AMOUNT_FROM : AMOUNT_TO)
                .then(() => {
                    paymentMethodService.current.changePaymentMethod(props.method, props.code, () => {
                        props.returnToCalculator();
                    });
                });
        });
    };

    const clickHandler = (e) => {
        e.preventDefault();
        if (props.method === PAYMENT_ACH && props.clickHandler) {
            expectedGTLevel ? amplitude.getInstance().logEvent(INCREASE_LIMITS_SHOWN, { [GT_LEVEL]: expectedGTLevel }) : null;
            props.clickHandler(e, props.method, props.code);
        } else if (props.isTemporarilyUnavailableSource) {
            amplitude.getInstance().logEvent(METHOD_UNAVAILABLE_SHOWN, {
                [PAYMENT_COUNTRY]: userCountry?.nameEn,
                [PAYOUT_COUNTRY]: toCountry?.nameEn,
                [PAYMENT_CURRENCY]: fromCurrency?.code,
                [PAYOUT_CURRENCY]: toCurrency?.code,
                [PAYMENT_METHOD_TYPE]: PAYMENT,
            });
            popupRef.current?.open(
                <SimplePopup text={props.temporarilyUnavailableReason} popupRef={popupRef}>
                    <PopupButton
                        iconName="arrow-forward"
                        text={changePaymentOption}
                        onClick={() => {
                            popupRef.current?.close();
                        }}
                    />
                </SimplePopup>,
            );
        } else if (props.isDisabledForOfakim) {
            if (props.ofakimHandleKycFlow) {
                props.modalRef.current?.open(<OfakimDocsInfo modalRef={props.modalRef} ofakimHandleKycFlow={props.ofakimHandleKycFlow} />);
            } else {
                props.modalRef.current?.open(
                    <MainOfakimModal
                        onClickNotNow={(e) => {
                            e.preventDefault();
                            props.modalRef?.current?.close();
                        }}
                        toggleSpinner={props.toggleSpinner}
                        setNewForm={props.setNewForm}
                        addExtras={props.addExtras}
                        showIntro={true}
                        updatePageMessages={props.updatePageMessages}
                        onClickOfakimStatus={() => props.modalRef?.current?.close()}
                        insideModalComponent
                        preventCloseNavigation
                    />,
                );
            }
        } else {
            popupRef.current?.open(
                <SimplePopup text={popupText} popupRef={popupRef}>
                    <PopupButton
                        iconName="arrow-back"
                        text={changeAmount}
                        onClick={(event) => {
                            event.preventDefault();
                            if (props.returnToCalculator) {
                                updateAmount(props.fromAmountIssue);
                            }
                            popupRef.current?.close();
                        }}
                    />
                    <PopupButton
                        iconName="arrow-forward"
                        text={changePaymentOption}
                        onClick={() => {
                            popupRef.current?.close();
                        }}
                    />
                </SimplePopup>,
            );
        }
    };

    return (
        <>
            <Panel onClick={clickHandler} negativeMargin>
                <PanelFrame>
                    <DiskBadgeWrapper>
                        <DiskBadge
                            iconName={props.icon ? props.icon : null}
                            src={props.src ? props.src : null}
                            variant={props.diskVariant ? props.diskVariant : 'secondary'}
                            withoutRadius={props.withoutRadius}
                            isInactive
                            isDisabled
                        />
                    </DiskBadgeWrapper>
                    <PanelContent>{getTitle(getName(props.method, props.paymentMethod, props.name), props.description)}</PanelContent>
                    <PanelRightBlock>
                        <DiskButton type="button" variant="tertiary" size="small" iconName="info-rounded" iconColor="lightGray" raised={false} />
                    </PanelRightBlock>
                </PanelFrame>
            </Panel>
            <PopupNew ref={popupRef} />
        </>
    );
};

export default connect(null, {
    addExtras,
    changeFormFieldValue,
    setNewForm,
    toggleSpinner,
    updateExtraByName,
    updateFieldValues,
    updateFormProperties,
    updatePageMessages,
})(DisabledPaymentMethod);
